import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { FreelancerContext } from './FreelancerEntity';
import { ShadowContainer } from '../../common/ShadowContainer';
import { Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { TEXTS } from '../../../core/texts';
import { BoxFullWidth, FlexBoxFullWidth } from '../../common/boxes';
import { Close, Edit } from '@mui/icons-material';
import { useGetRolesWithPositionsAndSkills } from '../../../queryHooks/useGetRolesWithPositionsAndSkills';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { TagsAutocomplete } from '../../common/TagsAutocomplete';
import { ButtonWithLoader } from '../../common/ButtonWithLoader';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IAdminUpdateFreelancerProfile } from '../../../interfaces/adminUpdateFreelancerProfile';
import { FreelancerService } from '../../../services/freelancer';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../queryHooks/freelancer/useGetFreelancerEntity';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../services/axios';
import { Validator } from '../../../core/Validator';
import NAMES = Validator.NAMES;
import { IPositionWithSkills } from '../../../interfaces/position';
import { IFreelancerProfile } from '../../../interfaces/freelancerProfile';
import { useNotification } from '../../../+state/useNotification';

const getStateFromUser = (profile: IFreelancerProfile): IAdminUpdateFreelancerProfile => ({
  roleId: profile.roleId || '' as any,
  positionId: profile.positionId || '' as any,
});

const FreelancerSkills = () => {
  const { user: { freelancerProfile } } = useContext(FreelancerContext);
  const queryClient = useQueryClient();
  const { openErrorToast, openSuccessToast } = useNotification();
  const [editMode, setEditMode] = useState(false);
  const [state, setState] = useState<IAdminUpdateFreelancerProfile>(getStateFromUser(freelancerProfile));
  const [skills, setSkills] = useState<string[]>(freelancerProfile.skills);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  const { data: rolesWithPositionsAndSkills } = useGetRolesWithPositionsAndSkills();
  const rolesList = useMemo(() => rolesWithPositionsAndSkills || [], [rolesWithPositionsAndSkills]);
  const handleRoleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setState(prev => ({ ...prev, roleId: +value, positionId: '' as any }));
  }, []);

  const handlePositionsChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setState(prev => ({ ...prev, positionId: +value }));
  }, []);

  const allPositionsList = useMemo(() => rolesList
      .reduce((acc: IPositionWithSkills[], cur) => acc.concat(cur.positions), []),
    [rolesList]);

  const positionsList = useMemo(() => editMode ? allPositionsList.filter(r => r.roleId === state.roleId) : allPositionsList,
    [allPositionsList, editMode, state.roleId]);

  const skillsList = useMemo(() =>
    new Set(rolesList.reduce((acc: string[], cur) =>
      acc.concat(...cur.positions.map(p => p.skills.reduce((skills: string[], skill) => skills.concat(skill.name), []))), [])
    ), [rolesList]);

  const toggleEditMode = useCallback(() => setEditMode(prev => {
    setSkills(freelancerProfile.skills);
    setState(getStateFromUser(freelancerProfile));
    return !prev;
  }), [freelancerProfile]);

  const mutationRoles = useMutation({
    mutationFn: (update: IAdminUpdateFreelancerProfile) => FreelancerService.editProfile(freelancerProfile.id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
      setEditMode(false);
      setState(getStateFromUser(data.freelancerProfile));
      openSuccessToast('Successfully updated');
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const mutationSkills = useMutation({
    mutationFn: ({ updatedSkills, toRemove }: { updatedSkills: string[], toRemove?: boolean }) => toRemove
      ? FreelancerService.removeSkills(freelancerProfile.id, updatedSkills)
      : FreelancerService.addSkills(freelancerProfile.id, updatedSkills),
    onSuccess: (data) => {
      setEditMode(false);
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const hasChanges = useMemo(() =>
      state.roleId !== freelancerProfile.roleId
      || state.positionId !== freelancerProfile.positionId
      || (!!(freelancerProfile.skills || []).find(s => !skills.map(s => s.toLowerCase()).includes(s.toLowerCase())) || freelancerProfile.skills?.length !== skills.length)
    , [state, freelancerProfile.skills, freelancerProfile.positionId, freelancerProfile.roleId, skills]);

  const handleSubmitBtn = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validErrors = Validator.validate(state, NAMES.PROFILE);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    if (state.roleId !== freelancerProfile.roleId || state.positionId !== freelancerProfile.positionId) {
      mutationRoles.mutate(state);
    }
    const toRemove = freelancerProfile.skills.filter(s => !skills.includes(s));
    const toAdd = skills.filter(s => !freelancerProfile.skills.includes(s));
    if (toRemove.length) {
      mutationSkills.mutate({ updatedSkills: toRemove, toRemove: true });
    }
    if (toAdd.length) {
      mutationSkills.mutate({ updatedSkills: toAdd });
    }
  }, [mutationRoles, mutationSkills, skills, state, freelancerProfile.skills, freelancerProfile.roleId, freelancerProfile.positionId]);

  return (
    <ShadowContainer width={'100%'}>
      <form style={{ width: '100%' }} onSubmit={handleSubmitBtn}>
        <FlexBoxFullWidth justifyContent={'space-between'}>
          <Typography variant={'h3'}>
            {TEXTS.POSITION_AND_SKILLS}
          </Typography>
          <Tooltip title={editMode ? TEXTS.CANCEL : TEXTS.EDIT}>
            <IconButton onClick={toggleEditMode}>
              {
                editMode
                  ? <Close/>
                  : <Edit color={'primary'}/>
              }
            </IconButton>
          </Tooltip>
        </FlexBoxFullWidth>
        {
          !!rolesList.length &&
          <TextField name={'roleId'} value={editMode ? (state.roleId || '') : freelancerProfile.roleId || ''}
                     label={TEXTS.ROLE}
                     select
                     variant={editMode ? 'outlined' : 'standard'}
                     error={!!validationErrors.roleId} helperText={validationErrors.roleId} disabled={!editMode}
                     onChange={handleRoleChange} sx={{ width: '100%', my: 2 }}
          >
            {
              rolesList.map(role => (
                <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
              ))
            }
          </TextField>
        }
        {
          !!positionsList.length &&
          <TextField name={'positionId'}
                     value={editMode ? (state.positionId || '') : freelancerProfile.positionId || ''}
                     label={TEXTS.POSITION}
                     select disabled={!editMode}
                     error={!!validationErrors.positionId} helperText={validationErrors.positionId}
                     onChange={handlePositionsChange} sx={{ width: '100%', my: 2 }}
                     variant={editMode ? 'outlined' : 'standard'}
          >
            {
              positionsList.map(pos => (
                <MenuItem key={pos.id} value={pos.id}>{pos.name}</MenuItem>
              ))
            }
          </TextField>
        }


        <BoxFullWidth>
          <TagsAutocomplete disabled={!editMode} list={[...skillsList]} onChange={setSkills}
                            value={editMode ? skills : freelancerProfile.skills || []}
                            label={TEXTS.SKILLS}/>
        </BoxFullWidth>
        {
          editMode &&
          <FlexBoxFullWidth justifyContent={'space-between'} p={1} mt={3}>
            <Button color={'error'} onClick={toggleEditMode} variant={'outlined'}>{TEXTS.CANCEL}</Button>
            <ButtonWithLoader disabled={!hasChanges} type={'submit'} loading={mutationRoles.isLoading}
                              variant={'outlined'}>
              {TEXTS.SAVE}
            </ButtonWithLoader>
          </FlexBoxFullWidth>
        }
      </form>
    </ShadowContainer>
  );
};

export default FreelancerSkills;